import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import type { RootState, AppDispatch } from '../../store';
import {
  UserCircleIcon,
  EnvelopeIcon,
  KeyIcon,
  ShieldCheckIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '../../components/ui/Button';
import Card, { CardContent, CardHeader } from '../../components/ui/Card';
import FormInput from '../../components/ui/FormInput';
// import FileUpload from '../../components/upload/FileUpload';
// import { authService } from '../../services/authService';
import { logout } from '../../store/slices/authSlice';
import SettingsSessions from './SettingsSessions';
import Settings2FA from './Settings2FA';
import { gdprService } from '../../services/gdprService';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  bio: string;
  website: string;
  location: string;
  // Banner is handled separately but we keep placeholder for preview toggles
}

interface CreatorSocialFormData {
  displayName: string;
  twitterHandle: string;
  instagramHandle: string;
  youtubeUrl: string;
  tiktokHandle: string;
  discordUrl: string;
  thankYouMessage: string;
  customUrl: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface EmailFormData {
  newEmail: string;
  password: string;
}

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'email' | 'sessions' | '2fa' | 'data-localization' | 'gdpr' | 'account'>('profile');
  // const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // GDPR state
  const [gdprRequests, setGdprRequests] = useState<any[]>([]);
  const [gdprLoading, setGdprLoading] = useState<boolean>(false);

  // Data localization state
  const [dataRegions, setDataRegions] = useState<any[]>([]);
  const [userDataSettings, setUserDataSettings] = useState<any>(null);
  const [dataMigrations, setDataMigrations] = useState<any[]>([]);
  const [dataLocalizationLoading, setDataLocalizationLoading] = useState<boolean>(false);
  
  // Profile form
  const [profileData, setProfileData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    bio: '',
    website: '',
    location: '',
  });
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);

  // Creator social/profile (if user is a creator)
  const [creatorProfileLoading, setCreatorProfileLoading] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [creatorSocialData, setCreatorSocialData] = useState<CreatorSocialFormData>({
    displayName: '',
    twitterHandle: '',
    instagramHandle: '',
    youtubeUrl: '',
    tiktokHandle: '',
    discordUrl: '',
    thankYouMessage: '',
    customUrl: ''
  });

  // Password form
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Email form
  const [emailData, setEmailData] = useState<EmailFormData>({
    newEmail: '',
    password: '',
  });

  // Account deletion
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        bio: user.bio || '',
        website: user.website || '',
        location: user.location || ''
      });
      // avatar preview initial
      if (user.avatar) setAvatarPreview(user.avatar);
    }
  }, [user]);

  // Handle OAuth redirects (e.g., Discord linking)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const platform = params.get('platform');
    const oauthError = params.get('error');
    const oauthSuccess = params.get('success');
    if (platform && (oauthError || oauthSuccess)) {
      if (oauthSuccess) setSuccess(`${platform} account linked successfully.`);
      if (oauthError) setError(`Failed to link ${platform} account: ${oauthError}`);
      const url = new URL(window.location.href);
      url.searchParams.delete('platform');
      url.searchParams.delete('error');
      url.searchParams.delete('success');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Load creator profile data if exists (only when profile tab active to reduce load)
  useEffect(() => {
    const loadCreator = async () => {
      if (activeTab !== 'profile') return;
      try {
        setCreatorProfileLoading(true);
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        // Lazy import to avoid circular dependencies
        const { creatorService } = await import('../../services/creatorService');
        try {
          const profile = await creatorService.getMyCreatorProfile();
          if (profile && profile.id) {
            setIsCreator(true);
            setCreatorSocialData({
              displayName: profile.displayName || '',
              twitterHandle: profile.twitterHandle || '',
              instagramHandle: profile.instagramHandle || '',
              youtubeUrl: profile.youtubeUrl || '',
              tiktokHandle: profile.tiktokHandle || '',
              discordUrl: profile.discordUrl || '',
              thankYouMessage: profile.thankYouMessage || '',
              customUrl: profile.customUrl || ''
            });
            if (profile.banner) setBannerPreview(profile.banner);
          }
        } catch {
          // Not a creator, ignore
          setIsCreator(false);
        }
      } finally {
        setCreatorProfileLoading(false);
      }
    };
    loadCreator();
  }, [activeTab]);

  // Generate live profile preview card data
  const livePreview = useMemo(() => {
    return {
      name: (isCreator ? creatorSocialData.displayName : (user?.firstName || user?.username || 'User')) || 'User',
      bio: profileData.bio,
      avatar: avatarPreview,
      banner: bannerPreview,
      social: {
        twitter: creatorSocialData.twitterHandle,
        instagram: creatorSocialData.instagramHandle,
        youtube: creatorSocialData.youtubeUrl,
        tiktok: creatorSocialData.tiktokHandle,
        discord: creatorSocialData.discordUrl
      }
    };
  }, [isCreator, creatorSocialData, profileData.bio, avatarPreview, bannerPreview, user]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (avatarPreview && avatarFile) URL.revokeObjectURL(avatarPreview);
      if (bannerPreview && bannerFile) URL.revokeObjectURL(bannerPreview);
    };
  }, [avatarPreview, bannerPreview, avatarFile, bannerFile]);

  // Load GDPR requests when Privacy & Data tab is active
  useEffect(() => {
    const load = async () => {
      if (activeTab !== 'gdpr') return;
      try {
        setGdprLoading(true);
        setError(null);
        const reqs = await gdprService.listRequests();
        setGdprRequests(reqs);
      } catch (e: any) {
        setError(e?.message || 'Failed to load GDPR requests');
      } finally {
        setGdprLoading(false);
      }
    };
    load();
  }, [activeTab]);

  // Load data localization data when Data & Region tab is active
  useEffect(() => {
    const load = async () => {
      if (activeTab !== 'data-localization') return;
      try {
        setDataLocalizationLoading(true);
        setError(null);

        // Load available data regions
        const regionsRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/data-localization/regions`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        if (regionsRes.ok) {
          const regions = await regionsRes.json();
          setDataRegions(regions);
        }

        // Load user data settings
        const settingsRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/data-localization/user-settings`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setUserDataSettings(settings);
        }

        // Load user's data migrations
        const migrationsRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/data-localization/migrations`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
        if (migrationsRes.ok) {
          const migrationsData = await migrationsRes.json();
          setDataMigrations(migrationsData.migrations || []);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load data localization settings');
      } finally {
        setDataLocalizationLoading(false);
      }
    };
    load();
  }, [activeTab]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('accessToken');
      // User profile update
      const userForm = new FormData();
      userForm.append('firstName', profileData.firstName);
      userForm.append('lastName', profileData.lastName);
      if (profileData.bio) userForm.append('bio', profileData.bio);
      if (profileData.website) userForm.append('website', profileData.website);
      if (profileData.location) userForm.append('location', profileData.location);
      if (avatarFile) userForm.append('avatar', avatarFile);

      const userRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/users/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: userForm
      });
      if (!userRes.ok) {
        const error = await userRes.json();
        throw new Error(error.message || 'Failed to update user profile');
      }
      await userRes.json();

      // Creator profile update if creator
      if (isCreator) {
        const creatorForm = new FormData();
        if (creatorSocialData.displayName) creatorForm.append('displayName', creatorSocialData.displayName);
        if (creatorSocialData.twitterHandle) creatorForm.append('twitterHandle', creatorSocialData.twitterHandle);
        if (creatorSocialData.instagramHandle) creatorForm.append('instagramHandle', creatorSocialData.instagramHandle);
        if (creatorSocialData.youtubeUrl) creatorForm.append('youtubeUrl', creatorSocialData.youtubeUrl);
        if (creatorSocialData.tiktokHandle) creatorForm.append('tiktokHandle', creatorSocialData.tiktokHandle);
        if (creatorSocialData.discordUrl) creatorForm.append('discordUrl', creatorSocialData.discordUrl);
        if (creatorSocialData.thankYouMessage) creatorForm.append('thankYouMessage', creatorSocialData.thankYouMessage);
        if (creatorSocialData.customUrl) creatorForm.append('customUrl', creatorSocialData.customUrl);
        if (bannerFile) creatorForm.append('banner', bannerFile);

        const creatorRes = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/creators/profile`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: creatorForm
        });
        if (!creatorRes.ok) {
          const error = await creatorRes.json();
          throw new Error(error.message || 'Failed to update creator profile');
        }
        await creatorRes.json();
      }

      setSuccess(isCreator ? 'Profile & creator settings updated successfully!' : 'Profile updated successfully!');
      // Reload user data
      window.location.reload();
    } catch (err: any) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setSubmitting(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/users/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to update password');
      }

      setSuccess('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/users/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          newEmail: emailData.newEmail,
          password: emailData.password,
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to update email');
      }

      setSuccess('Email updated successfully! Please check your inbox for verification.');
      setEmailData({
        newEmail: '',
        password: '',
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to update email');
    } finally {
      setSubmitting(false);
    }
  };

  // Start Discord OAuth linking
  const startDiscordLink = async () => {
    try {
      const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';
      const res = await fetch(`${base}/api/oauth/discord/authorize`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      if (!res.ok) throw new Error('Failed to start Discord authorization');
      const data = await res.json();
      if (data?.authUrl) {
        window.location.href = data.authUrl as string;
      } else {
        throw new Error('Authorization URL missing');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not initiate Discord linking');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE' && deleteConfirm !== user?.username) {
      setError('Please type DELETE or your username to confirm');
      return;
    }

    if (!window.confirm('Are you absolutely sure? This action cannot be undone!')) {
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402'}/api/users/account`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to delete account');
      }

      await dispatch(logout());
      navigate('/');
    } catch (err: any) {
      setError(err?.message || 'Failed to delete account');
      setDeleting(false);
    }
  };

  const handleExportData = async () => {
    try {
      await gdprService.exportData();
      setSuccess('Data exported successfully!');
    } catch (err: any) {
      setError(err?.message || 'Failed to export data');
    }
  };

  const handleGDPRDelete = async () => {
    const password = prompt('Enter your password to confirm account deletion:');
    if (!password) return;
    try {
      const res = await gdprService.requestDeletion(password);
      setSuccess(res.message);
      // Refresh requests
      const reqs = await gdprService.listRequests();
      setGdprRequests(reqs);
    } catch (err: any) {
      setError(err?.message || 'Failed to request deletion');
    }
  };

  const handleGDPRCancelDeletion = async () => {
    if (!window.confirm('Cancel your scheduled account deletion?')) return;
    try {
      const res = await gdprService.cancelDeletion();
      setSuccess(res.message);
      const reqs = await gdprService.listRequests();
      setGdprRequests(reqs);
    } catch (err: any) {
      setError(err?.message || 'Failed to cancel deletion');
    }
  };

  const handleCreateExportRequest = async () => {
    try {
      await gdprService.createExportRequest();
      setSuccess('Export request created. You will be notified when it is ready.');
      const reqs = await gdprService.listRequests();
      setGdprRequests(reqs);
    } catch (err: any) {
      setError(err?.message || 'Failed to create export request');
    }
  };

  const handleDownloadExportById = async (id: string) => {
    try {
      await gdprService.downloadRequest(id);
      setSuccess('Download started.');
    } catch (err: any) {
      setError(err?.message || 'Failed to download export');
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserCircleIcon },
    { id: 'password', name: 'Password', icon: KeyIcon },
    { id: 'email', name: 'Email', icon: EnvelopeIcon },
    { id: 'sessions', name: 'Sessions', icon: ShieldCheckIcon },
    { id: '2fa', name: '2FA', icon: ShieldCheckIcon },
    { id: 'data-localization', name: 'Data & Region', icon: ShieldCheckIcon },
    { id: 'gdpr', name: 'Privacy & Data', icon: ShieldCheckIcon },
    { id: 'account', name: 'Account', icon: TrashIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Helmet>
        <title>Settings - Patreon Clone</title>
        <meta name="description" content="Manage your account settings on Patreon Clone." />
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-4">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'text-white'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      style={activeTab === tab.id ? { background: 'var(--patreon-btn-primary-bg)', boxShadow: 'var(--patreon-btn-primary-shadow)' } : undefined}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {tab.name}
                    </button>
                  );
                })}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
              <p className="text-sm" style={{ color: 'var(--patreon-error)' }}>{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-lg bg-green-50 border border-green-200">
              <p className="text-sm text-green-800 flex items-center">
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                {success}
              </p>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-gray-900">Profile Settings</h2>
                <p className="text-sm text-gray-600 mt-1">Update your public profile information</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-6">
                  <div className="flex items-center space-x-6">
                    <div className="flex-shrink-0">
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt={user?.username || 'avatar preview'}
                          className="w-24 h-24 rounded-full object-cover ring-2 ring-orange-400"
                        />
                      ) : (
                        <div
                          className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold patreon-primary-gradient"
                          style={{ background: 'var(--patreon-btn-primary-bg)' }}
                        >
                          {(user?.firstName || user?.username || 'U').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setAvatarFile(file);
                            const previewUrl = URL.createObjectURL(file);
                            setAvatarPreview(previewUrl);
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:cursor-pointer"
                      />
                      {avatarFile && (
                        <p className="mt-1 text-xs text-gray-500">Selected: {avatarFile.name}</p>
                      )}
                    </div>
                  </div>

                  {isCreator && (
                    <div className="flex items-center space-x-6">
                      <div className="flex-shrink-0 w-32">
                        {bannerPreview ? (
                          <img
                            src={bannerPreview}
                            alt="Banner preview"
                            className="w-32 h-20 object-cover rounded-lg ring-1 ring-gray-200"
                          />
                        ) : (
                          <div className="w-32 h-20 rounded-lg bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center text-white text-sm font-semibold">
                            Banner
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Creator Banner (optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setBannerFile(file);
                              const previewUrl = URL.createObjectURL(file);
                              setBannerPreview(previewUrl);
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:cursor-pointer"
                        />
                        {bannerFile && (
                          <p className="mt-1 text-xs text-gray-500">Selected: {bannerFile.name}</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInput
                      label="First Name"
                      value={profileData.firstName}
                      onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                      placeholder="Enter your first name"
                    />

                    <FormInput
                      label="Last Name"
                      value={profileData.lastName}
                      onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                      placeholder="Enter your last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                    <textarea
                      value={profileData.bio}
                      onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                      placeholder="Tell us about yourself..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-offset-2"
                      style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                      onFocus={(e) => { e.target.style.borderColor = 'var(--patreon-orange)'; e.target.style.boxShadow = '0 0 0 3px rgba(255, 107, 53, 0.1)'; }}
                      onBlur={(e) => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
                      rows={4}
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-500">{profileData.bio.length}/500</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInput
                      label="Website"
                      type="url"
                      value={profileData.website}
                      onChange={(e) => setProfileData({ ...profileData, website: e.target.value })}
                      placeholder="https://yourwebsite.com"
                    />

                    <FormInput
                      label="Location"
                      value={profileData.location}
                      onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                      placeholder="City, Country"
                    />
                  </div>

                  {isCreator && (
                    <div className="space-y-6 pt-4 border-t">
                      <h3 className="text-md font-semibold text-gray-900">Creator Social & Branding</h3>
                      {creatorProfileLoading && (
                        <p className="text-sm text-gray-500">Loading creator profile...</p>
                      )}
                      {!creatorProfileLoading && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <FormInput
                            label="Display Name"
                            value={creatorSocialData.displayName}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, displayName: e.target.value })}
                            placeholder="Public display name"
                          />
                          <FormInput
                            label="Custom URL"
                            value={creatorSocialData.customUrl}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, customUrl: e.target.value })}
                            placeholder="my-channel"
                          />
                          <FormInput
                            label="Twitter Handle"
                            value={creatorSocialData.twitterHandle}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, twitterHandle: e.target.value })}
                            placeholder="@yourhandle"
                          />
                          <FormInput
                            label="Instagram Handle"
                            value={creatorSocialData.instagramHandle}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, instagramHandle: e.target.value })}
                            placeholder="@yourinsta"
                          />
                          <FormInput
                            label="YouTube URL"
                            type="url"
                            value={creatorSocialData.youtubeUrl}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, youtubeUrl: e.target.value })}
                            placeholder="https://youtube.com/yourchannel"
                          />
                          <FormInput
                            label="TikTok Handle"
                            value={creatorSocialData.tiktokHandle}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, tiktokHandle: e.target.value })}
                            placeholder="@yourtiktok"
                          />
                          <FormInput
                            label="Discord Invite URL"
                            type="url"
                            value={creatorSocialData.discordUrl}
                            onChange={(e) => setCreatorSocialData({ ...creatorSocialData, discordUrl: e.target.value })}
                            placeholder="https://discord.gg/..."
                          />
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Discord Account</label>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={startDiscordLink}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition"
                                aria-label="Link Discord account"
                              >
                                Link Discord Account
                              </button>
                              <span className="text-sm text-gray-500">Use OAuth to link your Discord user for role sync and perks.</span>
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Thank You Message</label>
                            <textarea
                              value={creatorSocialData.thankYouMessage}
                              onChange={(e) => setCreatorSocialData({ ...creatorSocialData, thankYouMessage: e.target.value })}
                              rows={3}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-2 focus:ring-offset-2"
                              style={{ '--tw-ring-color': 'var(--patreon-orange)' } as React.CSSProperties}
                              placeholder="Message shown to new patrons"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Live Preview */}
                  <div className="pt-8 border-t">
                    <h3 className="text-md font-semibold text-gray-900 mb-4">Live Preview</h3>
                    <div className="rounded-xl border shadow-sm overflow-hidden bg-white">
                      {livePreview.banner && (
                        <div className="h-32 w-full bg-gray-200 relative">
                          <img src={livePreview.banner} alt="Banner" className="h-32 w-full object-cover" />
                          <div className="absolute bottom-2 left-4 flex items-center space-x-3">
                            {livePreview.avatar ? (
                              <img src={livePreview.avatar} alt="Avatar" className="w-16 h-16 rounded-full border-2 border-white object-cover" />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center text-white text-xl font-bold">
                                {livePreview.name.charAt(0)}
                              </div>
                            )}
                            <div>
                              <p className="text-lg font-semibold text-white drop-shadow">{livePreview.name}</p>
                              {livePreview.social.twitter && (
                                <p className="text-xs text-white/90">{livePreview.social.twitter}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {!livePreview.banner && (
                        <div className="p-4 flex items-center space-x-4">
                          {livePreview.avatar ? (
                            <img src={livePreview.avatar} alt="Avatar" className="w-16 h-16 rounded-full object-cover" />
                          ) : (
                            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-400 to-pink-500 flex items-center justify-center text-white text-xl font-bold">
                              {livePreview.name.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="text-lg font-semibold">{livePreview.name}</p>
                            {livePreview.social.twitter && (
                              <p className="text-xs text-gray-500">{livePreview.social.twitter}</p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="p-4">
                        <p className="text-sm text-gray-700 whitespace-pre-line min-h-[48px]">{livePreview.bio || 'Your bio will appear here.'}</p>
                        <div className="flex flex-wrap gap-2 mt-4">
                          {livePreview.social.twitter && (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700">Twitter</span>
                          )}
                          {livePreview.social.instagram && (
                            <span className="px-2 py-1 text-xs rounded-full bg-pink-50 text-pink-700">Instagram</span>
                          )}
                          {livePreview.social.youtube && (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-50 text-red-700">YouTube</span>
                          )}
                          {livePreview.social.tiktok && (
                            <span className="px-2 py-1 text-xs rounded-full bg-black text-white">TikTok</span>
                          )}
                          {livePreview.social.discord && (
                            <span className="px-2 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700">Discord</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="submit"
                      loading={submitting}
                      leftIcon={<CheckCircleIcon className="w-4 h-4" />}
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-gray-900">Change Password</h2>
                <p className="text-sm text-gray-600 mt-1">Update your password to keep your account secure</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordUpdate} className="space-y-6">
                  <FormInput
                    label="Current Password"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    placeholder="Enter your current password"
                    required
                  />

                  <FormInput
                    label="New Password"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Enter your new password (min. 8 characters)"
                    required
                  />

                  <FormInput
                    label="Confirm New Password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Confirm your new password"
                    required
                  />

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="submit"
                      loading={submitting}
                      leftIcon={<KeyIcon className="w-4 h-4" />}
                    >
                      Update Password
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-gray-900">Change Email</h2>
                <p className="text-sm text-gray-600 mt-1">Update your email address</p>
                {user?.email && (
                  <p className="text-sm text-gray-500 mt-1">Current: {user.email}</p>
                )}
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEmailUpdate} className="space-y-6">
                  <FormInput
                    label="New Email"
                    type="email"
                    value={emailData.newEmail}
                    onChange={(e) => setEmailData({ ...emailData, newEmail: e.target.value })}
                    placeholder="Enter your new email"
                    required
                  />

                  <FormInput
                    label="Current Password"
                    type="password"
                    value={emailData.password}
                    onChange={(e) => setEmailData({ ...emailData, password: e.target.value })}
                    placeholder="Enter your password to confirm"
                    required
                  />

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="submit"
                      loading={submitting}
                      leftIcon={<EnvelopeIcon className="w-4 h-4" />}
                    >
                      Update Email
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <SettingsSessions />
          )}

          {/* 2FA Tab */}
          {activeTab === '2fa' && (
            <Settings2FA />
          )}

          {/* Data Localization Tab */}
          {activeTab === 'data-localization' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h2 className="text-xl font-semibold text-gray-900">Data Storage Region</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose where your data is stored and manage data residency preferences
                  </p>
                </CardHeader>
                <CardContent>
                  {dataLocalizationLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Current Data Region */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-2">Current Data Region</h3>
                        {userDataSettings?.preferredDataRegion ? (
                          <div className="p-4 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800">
                              Your data is currently stored in: <strong>{userDataSettings.preferredDataRegion}</strong>
                            </p>
                            {userDataSettings.dataResidencyConfirmed && (
                              <p className="text-sm text-blue-600 mt-1">
                                ✓ Data residency preference confirmed
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="p-4 bg-yellow-50 rounded-lg">
                            <p className="text-sm text-yellow-800">
                              No data region preference set. Your data is stored in our default region.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Available Regions */}
                      <div>
                        <h3 className="font-medium text-gray-900 mb-4">Available Data Regions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {dataRegions.map((region: any) => (
                            <div key={region.id} className="p-4 border rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium text-gray-900">{region.name}</h4>
                                {region.isDefault && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Default
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-2">
                                {region.country}, {region.continent}
                              </p>
                              {region.description && (
                                <p className="text-sm text-gray-500">{region.description}</p>
                              )}
                              {region.storageCost && region.storageCost > 0 && (
                                <p className="text-sm text-orange-600 mt-2">
                                  Additional storage cost: +{region.storageCost}%
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Data Migration Requests */}
                      {dataMigrations.length > 0 && (
                        <div>
                          <h3 className="font-medium text-gray-900 mb-4">Data Migration History</h3>
                          <div className="space-y-3">
                            {dataMigrations.slice(0, 5).map((migration: any) => (
                              <div key={migration.id} className="p-4 border rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium">
                                    {migration.sourceRegion.name} → {migration.targetRegion.name}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                    migration.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                    migration.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                                    migration.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {migration.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  Requested: {new Date(migration.requestedAt).toLocaleDateString()}
                                </p>
                                {migration.status === 'COMPLETED' && migration.completedAt && (
                                  <p className="text-sm text-green-600">
                                    Completed: {new Date(migration.completedAt).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Request Data Migration */}
                      <div className="pt-6 border-t">
                        <h3 className="font-medium text-gray-900 mb-2">Request Data Migration</h3>
                        <p className="text-sm text-gray-600 mb-4">
                          If you'd like to move your data to a different region, you can request a migration.
                          This process may take several days to complete.
                        </p>
                        <Button
                          onClick={() => {
                            // TODO: Implement migration request modal
                            alert('Migration request feature coming soon!');
                          }}
                          variant="outline"
                        >
                          Request Data Migration
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* GDPR Tab */}
          {activeTab === 'gdpr' && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-gray-900">Privacy & Data</h2>
                <p className="text-sm text-gray-600 mt-1">Manage your data and privacy settings</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Export Your Data</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Download all of your data stored on our platform in JSON format, or create an export request we'll prepare asynchronously.
                    </p>
                    <div className="flex gap-3 flex-wrap">
                      <Button onClick={handleExportData}>Export Data (instant)</Button>
                      <Button variant="outline" onClick={handleCreateExportRequest}>Request Export (asynchronous)</Button>
                      <button
                        onClick={async () => {
                          setGdprLoading(true);
                          try { setGdprRequests(await gdprService.listRequests()); } finally { setGdprLoading(false); }
                        }}
                        className="px-4 py-2 rounded border text-sm"
                      >Refresh Requests</button>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-medium text-gray-900 mb-2">Your GDPR Requests</h3>
                    <div className="overflow-x-auto bg-white border rounded">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Scheduled</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {gdprRequests.map((r) => (
                            <tr key={r.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm font-medium">{r.type}</td>
                              <td className="px-4 py-2 text-sm">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">{r.status}</span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700">{new Date(r.requestedAt).toLocaleString()}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{r.scheduledDeletionAt ? new Date(r.scheduledDeletionAt).toLocaleDateString() : '-'}</td>
                              <td className="px-4 py-2 text-sm flex gap-2">
                                {r.type === 'EXPORT' && (r.status === 'READY' || r.status === 'COMPLETED') && (
                                  <button onClick={() => handleDownloadExportById(r.id)} className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">Download</button>
                                )}
                                {r.type === 'DELETE' && r.status === 'PENDING' && (
                                  <button onClick={handleGDPRCancelDeletion} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
                                )}
                              </td>
                            </tr>
                          ))}
                          {gdprRequests.length === 0 && !gdprLoading && (
                            <tr>
                              <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">No GDPR requests yet</td>
                            </tr>
                          )}
                          {gdprLoading && (
                            <tr>
                              <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-medium text-gray-900 mb-2">Request Account Deletion</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Request permanent deletion of your account. This action will be processed after a 7-day cooldown period.
                    </p>
                    <Button variant="danger" onClick={handleGDPRDelete}>
                      Request Deletion
                    </Button>
                    {gdprRequests.some((r) => r.type === 'DELETE' && r.status === 'PENDING' && r.scheduledDeletionAt) && (
                      <p className="text-sm text-gray-600 mt-2">
                        Scheduled deletion date: {new Date(gdprRequests.find((r) => r.type === 'DELETE' && r.status === 'PENDING')?.scheduledDeletionAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <Card>
              <CardHeader>
                <h2 className="text-xl font-semibold text-gray-900">Delete Account</h2>
                <p className="text-sm text-gray-600 mt-1">Permanently delete your account and all associated data</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="font-semibold text-red-900 mb-2">Warning: This action cannot be undone</h3>
                    <p className="text-sm text-red-700">
                      Deleting your account will permanently remove all your data, including:
                    </p>
                    <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                      <li>Your profile and all posts</li>
                      <li>All memberships and subscriptions</li>
                      <li>All comments, likes, and interactions</li>
                      <li>Your creator profile (if applicable)</li>
                      <li>All payment history</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type <strong>DELETE</strong> or your username <strong>{user?.username}</strong> to confirm:
                    </label>
                    <FormInput
                      type="text"
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder="Type DELETE or your username"
                      className="mb-4"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={handleDeleteAccount}
                      loading={deleting}
                      disabled={deleteConfirm !== 'DELETE' && deleteConfirm !== user?.username}
                      leftIcon={<TrashIcon className="w-4 h-4" />}
                      className="border-red-600 text-red-600 hover:bg-red-50"
                    >
                      Delete My Account
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;

